"use strict";

/**
 * Basic utilities module.
 * @module utils/basic
 */

/* Gets value in object path
 * @param {object} obj
 * @param {string|array} path
 * @examples
 * get({foo: 1, bar: {baz: 2}}, 'bar/baz');
 * > 2
 * get({foo: 1, bar: {baz: 2}}, 'foo/bar/baz/quax');
 * > undefined
 */
function objectGet(obj, path) {
  if (typeof path === "string") {
    path = path.split("/");
  }
  var prop = path.slice(-1);
  var node = path.slice(0, -1).reduce(function(node, item) {
    return node[item] || {};
  }, obj);
  return node[prop];
}

/**
 * Sets value in object path
 * @param {object} obj
 * @param {string|array} path
 * @param {*} value
 * @examples
 * set({foo: 1}, 'bar/baz', 2);
 * // > {foo: 1, bar: {baz: 2}}
 */
function objectSet(obj, path, value) {
  if (typeof path === "string") {
    path = path.split("/");
  }
  var prop = path.slice(-1);
  var node = path.slice(0, -1).reduce(function(node, item) {
    return (node[item] = node[item] || {});
  }, obj);
  node[prop] = value;
  return obj;
}

/**
 * Given an object, return an array of its property values.
 * @param {Object} object
 */
function objectValues(object) {
  return Object.keys(object).map(function(key) {
    return object[key];
  });
}

/**
 * Slices an object given a path.
 */
function objectSlice(obj, path) {
  path = path || [];
  return objectSet({}, path, objectGet(obj, path));
}

function cloneArray(array) {
  return array.slice(0);
}

function cloneDate(date) {
  return new Date(date.getTime());
}

/**
 * Returns new deeply merged JSON-parsed objects.
 * @param {...object} obj - Object to perform deep merge
 * @returns {object} Deeply merged object. This returned object can be safely modified without
 * causing any change on any passed source objects.
 *
 * @example
 * merge( { a: { b: 1, c: 2 } }, { a: { b: 3, d: 4 } }, ... )
 * // > { a: { b: 3, c: 2, d: 4 } }
 *
 * Code from https://github.com/rxaviers/cldrjs/blob/master/src/util/json/merge.js
 */
function jsonDeepMerge(/* obj */) {
  var destination = {};
  var sources = [].slice.call(arguments, 0);
  sources.forEach(function(source) {
    var prop;
    for (prop in source) {
      if (Array.isArray(source[prop])) {
        // Clone and set arrays.
        destination[prop] = cloneArray(source[prop]);
      } else if (source[prop] instanceof Date) {
        // Clone and set dates.
        destination[prop] = cloneDate(source[prop]);
      } else if (typeof source[prop] === "object") {
        // Merge objects.
        destination[prop] = jsonDeepMerge({}, destination[prop], source[prop]);
      } else {
        // Set new values.
        destination[prop] = source[prop];
      }
    }
  });
  return destination;
}

/**
 * Oneliner for Object.defineProperty() using simple `value` descriptor.
 * @param {Object} object The object on which to define the property.
 * @param {string} property The name of the property to be defined or modified.
 * @param {*} value The value associated with the property. Can be any valid JavaScript value (number, object, function, etc).
 */
function definePrivateProperty(object, property, value) {
  Object.defineProperty(object, property, {
    value: value
  });
}

/**
 * TODO description.
 */
function defineProperties(handler, properties) {
  var propertiesNames = Object.keys(properties);
  propertiesNames.forEach(function(key) {
    Object.defineProperty(handler.prototype, key, {
      get: function() {
        return properties[key];
      },
      enumerable: true
    });
  });

  return objectValues(properties);
}

/**
 * Formats a message replacing placeholders with variables values.
 * @param {string} message - A message that can contain placeholders.
 * @param {Object} [variables] - Variables for replacing message placeholders.
 *
 * @example
 * formatMessage("Hello {name}", {name: "John"});
 * // > "Hello John"
 *
 * formatMessage("Hello {{name}}", {name: "John"});
 * // > "Hello \"John\""
 */
function formatMessage(message, variables) {
  return (
    message
      // Replace {{variable}} with `JSON.stringify(variableValue)`.
      .replace(/{{([0-9a-zA-Z-_. ]+)}}/g, function(_, variable) {
        // JSON.stringify(NaN) yields 'null', so we need a special case for NaN
        if (
          typeof variables[variable] === "number" &&
          isNaN(variables[variable])
        ) {
          return "NaN";
        }
        return JSON.stringify(variables[variable]);
      })
      // Replace {variable} with `variableValue`.
      .replace(/{([0-9a-zA-Z-_. ]+)}/g, function(_, variable) {
        return variables[variable];
      })
  );
}

/**
 * Checks if passed *object* is a plain object.
 * @param {*} object - Value subject to test.
 * @returns {boolean} Returns `true` if *object* is a plain object or `false`.
 *
 * @example
 *
 * isPlainObject(); // > false
 * isPlainObject(null); // > false
 * isPlainObject(new Date()); // > false
 * isPlainObject([]); // > false
 * isPlainObject(1); // > false
 * isPlainObject(true); // > false
 * isPlainObject("foo"); // > false
 * isPlainObject(function() {}); // > false
 * isPlainObject({a: 1}); // > true
 */
function isPlainObject(object) {
  // Inspired by jQuery Core, but reduced to our use case.
  return (
    object !== null &&
    "" + object === "[object Object]" &&
    object.constructor === Object
  );
}

/**
 * @param {Array} array - The list of strings to glue together as an English formatted list string.
 * @param {string} conjunction - Conjunction value, e.g., `'and'`, `'or'`.
 * @returns {string} Returns an English formatted list string using the passed *conjunction*.
 *
 * @example
 *
 * listFormat([], 'or'); // > ''
 * listFormat(['foo'], 'or'); // > 'foo'
 * listFormat(['foo', 'bar'], 'or'); // > 'foo or bar'
 * listFormat(['foo', 'bar', 'baz'], 'or'); // > 'foo, or bar, or baz'
 */
function listFormat(array, conjunction) {
  if (array.length === 0) {
    return "";
  }
  if (array.length === 1) {
    return array[0];
  }
  if (array.length === 2) {
    return array.join(" " + conjunction + " ");
  }
  if (array.length > 2) {
    return array.join(", " + conjunction + " ");
  }
}

/**
 * Returns the type of a variable with additional types than native `typeof`.
 * @param {*} variable - A variable to deduce its type.
 *
 * @example
 * smarterTypeof(null); // > "null"
 * smarterTypeof({a: 1}); // > "plain object"
 * smarterTypeof([]); // > "array"
 * smarterTypeof(new Date()); // > "date"
 * smarterTypeof(<anything else>); // > result of regular typeof.
 */
function smarterTypeof(variable) {
  if (variable === null) {
    return "null";
  }
  if (isPlainObject(variable)) {
    return "plain object";
  }
  if (Array.isArray(variable)) {
    return "array";
  }
  if (variable instanceof Date) {
    return "date";
  }
  return typeof variable;
}

/**
 * Returns `true` is *element* belongs to *collection*.
 * @param {*} element - The key of an Object or the element of an Array.
 * @param {(Array| Object)} Where to search for element at.
 *
 * @example
 *
 * withinRange("bar", ["foo", "bar", "baz"]);
 * // > true
 * withinRange("quax", ["foo", "bar", "baz"]);
 * // > false
 *
 * @example
 *
 * withinRange("bar", {foo: 1, bar: 2, baz: 3});
 * // > true
 * withinRange("quax", {foo: 1, bar: 2, baz: 3});
 * // > false
 */
function withinRange(element, collection) {
  return Array.isArray(collection)
    ? collection.indexOf(element) !== -1
    : element in collection;
}

/**
 * Given a value and a default for that value,
 * if value is undefined, return the default.
 * Otherwise, return the value itself.
 * @param {*} value the value which may or may not be undefined
 * @param {*} defaultValue the default for value
 *
 * @example
 *
 * getValueOrDefault('my string', 'default string');
 * // > 'my string'
 * getValueOrDefault(undefined, 'default string');
 * // > 'default string'
 */
function getValueOrDefault(value, defaultValue) {
  return value === undefined ? defaultValue : value;
}

/**
 * Given an object with properties and an array of valid string property names,
 * Clone the object and:
 * 1) Remove invalid properties,
 * 2) Remove non-string properties,
 * 3) Remove properties with only whitespace,
 * 4) Trim remaining string properties
 *
 * @param {Object} object the object to trim
 * @param {array} properties array of properties to preserve
 *
 * @return {Object} the cloned & trimmed object
 */
function trimObject(object, properties) {
  var clonedObject = {};
  properties.forEach(function(property) {
    var value = object[property];
    if (value === undefined) {
      return;
    }
    if (typeof value === "string") {
      value = value.trim();
    }
    clonedObject[property] = value;
  });
  return clonedObject;
}

/**
 * Given a string, return true if all the characters are Latin script (roughly < U+024F)
 * Note that empty strings are Latin by definition
 * @param {string} string to test
 * @return {boolean}
 */
function isLatin(string) {
  if (!string) {
    return true;
  }
  for (var i = 0; i < string.length; i++) {
    if (
      string.charCodeAt(i) > 0x24f &&
      (string.charCodeAt(i) < 0x2000 || string.charCodeAt(i) > 0x206f)
    ) {
      return false;
    }
  }
  return true;
}
/**
 * This version might be used in for loops and the like when
 * it is unknown whether non-BMP characters exist before the specified index position.
 * Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/charCodeAt
 * @param {string} string to get the character
 * @peram {number} the index position
 **/
function fixedCharCodeAt(str, idx) {
  // ex. fixedCharCodeAt('\uD800\uDC00', 0); // 65536
  // ex. fixedCharCodeAt('\uD800\uDC00', 1); // false
  idx = idx || 0;
  var code = str.charCodeAt(idx);
  var hi, low;

  // High surrogate (could change last hex to 0xDB7F
  // to treat high private surrogates
  // as single characters)
  if (0xd800 <= code && code <= 0xdbff) {
    hi = code;
    low = str.charCodeAt(idx + 1);
    if (isNaN(low)) {
      return hi;
    }
    return (hi - 0xd800) * 0x400 + (low - 0xdc00) + 0x10000;
  }
  if (0xdc00 <= code && code <= 0xdfff) {
    // Low surrogate
    // We return false to allow loops to skip
    // this iteration since should have already handled
    // high surrogate above in the previous iteration
    return false;
    // hi = str.charCodeAt(idx - 1);
    // low = code;
    // return ((hi - 0xD800) * 0x400) +
    //   (low - 0xDC00) + 0x10000;
  }
  return code;
}

function invalidCodePointFromString(string, regex) {
  var codePoint;
  for (var i = 0, chr; i < string.length; i++) {
    if (string[i].match(regex) === null) {
      if ((chr = fixedCharCodeAt(string, i)) === false) {
        continue;
      }
      codePoint = chr;
      /* we care only on the first one */
      break;
    }
  }

  return codePoint;
}

module.exports = {
  objectGet: objectGet,
  objectSet: objectSet,
  objectValues: objectValues,
  objectSlice: objectSlice,
  jsonDeepMerge: jsonDeepMerge,
  isLatin: isLatin,
  trimObject: trimObject,
  definePrivateProperty: definePrivateProperty,
  defineProperties: defineProperties,
  formatMessage: formatMessage,
  getValueOrDefault: getValueOrDefault,
  isPlainObject: isPlainObject,
  listFormat: listFormat,
  smarterTypeof: smarterTypeof,
  withinRange: withinRange,
  fixedCharCodeAt: fixedCharCodeAt,
  invalidCodePointFromString: invalidCodePointFromString
};



/*****************
 ** WEBPACK FOOTER
 ** ./~/griffin-paypal/lib/client/utils/basic.js
 ** module id = 57
 ** module chunks = 1 2 6 9 10 11 12
 **/